/**
 * Splitting an answer at its table boundaries.
 *
 * A turn that tabulates several things gets a download card per table, and each
 * has to render directly beneath the table it belongs to — a column of cards at
 * the end of the message gives the reader no way to tell which file is which.
 * So the markdown is rendered in segments and the cards are interleaved.
 *
 * Table detection matches the backend's: a header row and a delimiter row with
 * the SAME number of cells, outer pipes optional, because GitHub flavoured
 * markdown makes them optional and the model writes both styles.
 */

/** One run of markdown, plus the index of the table it ends with (if any). */
export type Segment = {
  text: string;
  /** 0-based index of the table this segment ends with, or null for trailing prose. */
  tableIndex: number | null;
};

/** Cells of a markdown row, with the optional outer pipes removed. */
function splitRow(line: string): string[] {
  return line
    .trim()
    .replace(/^\||\|$/g, "")
    .split("|")
    .map((cell) => cell.trim());
}

/** True for a delimiter row (|---|:--:|) of exactly this many cells. */
function isDelimiter(line: string, expectedCells: number): boolean {
  const cells = splitRow(line);
  return cells.length === expectedCells && cells.every((cell) => /^:?-+:?$/.test(cell));
}

/**
 * Split `content` so that each table ends a segment.
 *
 * Lossless: joining the segments with newlines reproduces the input exactly, so
 * rendering them in sequence renders the whole answer. Returns one segment with
 * `tableIndex: null` when there are no tables, so callers need no special case.
 */
export function splitByTables(content: string): Segment[] {
  if (!content || !content.includes("|")) return [{ text: content ?? "", tableIndex: null }];

  const lines = content.split("\n");
  const segments: Segment[] = [];
  let buffer: string[] = [];
  let tableCount = 0;
  let i = 0;

  while (i < lines.length) {
    const headerCells = lines[i].includes("|") ? splitRow(lines[i]) : [];
    const startsTable = headerCells.length >= 2 && i + 1 < lines.length && isDelimiter(lines[i + 1], headerCells.length);

    if (!startsTable) {
      buffer.push(lines[i]);
      i += 1;
      continue;
    }

    // Consume the whole table: header, delimiter, then every row of cells.
    buffer.push(lines[i], lines[i + 1]);
    let cursor = i + 2;
    while (cursor < lines.length) {
      const row = lines[cursor].trim();
      if (!row || !row.includes("|") || splitRow(row).length < 2) break;
      buffer.push(lines[cursor]);
      cursor += 1;
    }

    segments.push({ text: buffer.join("\n"), tableIndex: tableCount });
    buffer = [];
    tableCount += 1;
    i = cursor;
  }

  // Whatever follows the last table is its own segment, so it renders after that
  // table's card rather than before it.
  if (buffer.length > 0) segments.push({ text: buffer.join("\n"), tableIndex: null });
  return segments;
}

/**
 * The heading each table sits under, by table index: the last line of prose
 * before the table, stripped of markdown (heading hashes, emphasis, a trailing
 * colon). "" when a table has no line above it. Used to name a table's action
 * bar when an answer has several, since the bars render after the answer
 * rather than beneath their tables.
 */
export function tableTitles(content: string): string[] {
  return splitByTables(content)
    .filter((segment) => segment.tableIndex !== null)
    .map((segment) => {
      const lines = segment.text.split("\n");
      // The segment ends with its table; walk back to the first prose line above it.
      let index = lines.length - 1;
      while (index >= 0 && lines[index].includes("|")) index -= 1;
      while (index >= 0 && !lines[index].trim()) index -= 1;
      if (index < 0) return "";
      return lines[index]
        .replace(/^\s*(#{1,6}\s+|[-*+]\s+|\d+[.)]\s+)/, "")
        .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
        .replace(/[*_`]/g, "")
        .replace(/\s*:\s*$/, "")
        .trim();
    });
}

/** Data rows in the table with this index (header and delimiter rows excluded), 0 if there is no such table. */
export function tableRowCount(content: string, tableIndex: number): number {
  const segment = splitByTables(content).find((candidate) => candidate.tableIndex === tableIndex);
  if (!segment) return 0;
  const lines = segment.text.split("\n");
  let end = lines.length;
  // The segment ends with its table; count back through it.
  let start = end;
  while (start > 0 && lines[start - 1].includes("|") && lines[start - 1].trim()) start -= 1;
  return Math.max(0, end - start - 2);
}

/**
 * The data rows of the table that ends a segment, as cell text (header and
 * delimiter rows excluded). Row i here is the i-th <tr> of the rendered table,
 * which is how a row's checkbox finds the company it stands for.
 */
export function tableDataRows(segmentText: string): string[][] {
  const lines = segmentText.split("\n");
  let start = lines.length;
  while (start > 0 && lines[start - 1].includes("|") && lines[start - 1].trim()) start -= 1;
  return lines.slice(start + 2).map((line) =>
    splitRow(line).map((cell) =>
      cell
        .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
        .replace(/[*_`]/g, "")
        .trim()
    )
  );
}

/** Header cells of the table with this index (empty if there is no such table). */
export function tableHeaders(content: string, tableIndex: number): string[] {
  const segment = splitByTables(content).find((candidate) => candidate.tableIndex === tableIndex);
  if (!segment) return [];
  const lines = segment.text.split("\n");
  for (let i = lines.length - 2; i >= 0; i -= 1) {
    const cells = lines[i].includes("|") ? splitRow(lines[i]) : [];
    if (cells.length >= 2 && isDelimiter(lines[i + 1], cells.length)) return cells;
  }
  return [];
}
